import { useAtom, useAtomValue } from 'jotai';
import { flushSync } from 'react-dom';
import {
  demoModeAtom,
  domainChangeAnnouncementAtom,
  userAtom,
} from '@/global-store';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarInset,
  SidebarProvider,
} from '@layout/Sidebar/Sidebar';
import { Link as RouterLink, Outlet, useNavigation } from 'react-router-dom';
import { MessageCircleQuestion, MessageSquareText } from 'lucide-react';
import { useFetchAccessibilityData } from '@http/queries/useFetchAccessibilityData';
import MenuList from './Sidebar/MenuList/MenuList';
import Header from '@layout/Header/Header';
import { Button, useTailwindBreakpoint } from '@eye-able/ui';
import Footer from '@layout/Footer';
import LoadingSpinner from '@components/LoadingSpinner';
import { usePathname } from '@hooks/usePathname';
import { cn } from '@helpers/cn';
import { isInternalTeamUser } from '@helpers/isInternalTeamUser';
import { useInlineTranslation } from '@/i18n/hooks/useInlineTranslation.tsx';
import { FloatingDemoOfferActions } from './FloatingDemoOfferActions';
import AnnouncementBar from './AnnouncementBar';
import { useState } from 'react';
import { useSidebar } from '@layout/Sidebar/SidebarContext';

const SKIP_LINK_CLASSNAME =
  'eye:sr-only eye:focus:not-sr-only eye:focus:fixed eye:focus:top-8 eye:focus:left-8 eye:focus:z-2000 eye:focus:rounded-md eye:focus:bg-black eye:focus:text-white eye:focus:px-16 eye:focus:py-8 eye:focus:text-14 eye:focus:font-medium eye:focus:shadow-md eye:focus-visible:outline-white eye:focus-visible:-outline-offset-1';

function SkipLink({ href, onBeforeFocus, children }) {
  // A plain hash href would normally let the browser scroll+focus the
  // target natively, but react-router's <ScrollRestoration /> (mounted in
  // BaseRoute) manages scroll position on every history change and fights
  // that native behaviour. Handling the jump ourselves and preventing the
  // default navigation keeps the router out of it entirely.
  const handleClick = (event) => {
    event.preventDefault();
    // Runs synchronously (via flushSync where needed) so the target is no
    // longer inert/off-screen by the time we scroll and focus it below --
    // e.g. opening the mobile sidebar drawer before skipping into it.
    onBeforeFocus?.();

    const target = document.querySelector(href);
    if (!target) return;

    target.scrollIntoView({ block: 'start', behavior: 'instant' });
    target.focus({ preventScroll: true });
  };

  return (
    <a href={href} onClick={handleClick} className={SKIP_LINK_CLASSNAME}>
      {children}
    </a>
  );
}

export function MainLayout() {
  const xlAndUp = useTailwindBreakpoint('xl');

  return (
    <SidebarProvider
      className='eye:flex-col'
      style={{
        ['--app-header-content-height']: `${xlAndUp ? 75 : 52}px`,
      }}
    >
      <MainLayoutContent />
    </SidebarProvider>
  );
}

// Separate component so it can read the sidebar context that MainLayout provides.
function MainLayoutContent() {
  const [demoMode] = useAtom(demoModeAtom);
  const [user] = useAtom(userAtom);
  const { isMobile, openMobile, setOpenMobile } = useSidebar();

  const shouldShowDemoActions = demoMode && isMobile && !openMobile;
  const domainChangeAnnouncement = useAtomValue(domainChangeAnnouncementAtom);

  const { t } = useInlineTranslation();

  const path = usePathname();

  const isReportArea = path.startsWith('/report');
  const isRulesArea = path.startsWith('/rulesOverview');

  const { isLoading } = useFetchAccessibilityData();

  const navigation = useNavigation();
  const [announcementBarHeight, setAnnouncementBarHeight] = useState(0);

  const isPageLoading =
    navigation.state === 'loading' ||
    ((isReportArea || isRulesArea) && isLoading);

  const renderMainContent = () => {
    if (isPageLoading) {
      return <LoadingSpinner />;
    }
    return <Outlet />;
  };

  const shouldShowForInternalFeedback = isInternalTeamUser(user);

  return (
    <div
      className='eye:flex eye:flex-col eye:min-h-svh eye:w-full'
      style={{
        ['--app-announcement-height']: `${announcementBarHeight}px`,
        ['--app-header-height']:
          'calc(var(--app-announcement-height) + var(--app-header-content-height))',
      }}
    >
      <SkipLink href='#dashboard-header'>{t('skipToHeader')}</SkipLink>
      <SkipLink
        href='#dashboard-sidebar'
        onBeforeFocus={() => {
          // On mobile the sidebar is a closed off-canvas drawer: its content
          // is inert and off-screen until opened, so focus() would silently
          // fail. flushSync forces the drawer open before we scroll/focus.
          if (isMobile && !openMobile) {
            flushSync(() => setOpenMobile(true));
          }
        }}
      >
        {t('skipToSidebar')}
      </SkipLink>
      <SkipLink href='#main-content'>{t('skipToMainContent')}</SkipLink>
      <SkipLink href='#dashboard-footer'>{t('skipToFooter')}</SkipLink>
      <span aria-live='polite' role='status' className='eye:sr-only'>
        {domainChangeAnnouncement}
      </span>
      <AnnouncementBar onHeightChange={setAnnouncementBarHeight} />
      <Header />
      <div aria-hidden style={{ height: 'var(--app-header-height)' }} />
      <div
        className='eye:bg-grey-10 eye:flex eye:w-full eye:flex-1'
        style={{ paddingRight: 'var(--removed-body-scroll-bar-size, 0px)' }}
      >
        <Sidebar
          className='no-print'
          collapsible='icon'
          side='left'
          variant='inset'
        >
          <SidebarContent
            id='dashboard-sidebar'
            tabIndex={-1}
            className='eye:bg-white eye:outline-none'
            aria-label={t('mainNavigation')}
          >
            <MenuList />
          </SidebarContent>
          {!demoMode && (
            <SidebarFooter aria-label={t('sidebarToolsNavigation')}>
              <ul className='eye:flex eye:flex-col eye:gap-xs'>
                {shouldShowForInternalFeedback && (
                  <li>
                    <Button className='eye:w-full' variant='outline' asChild>
                      <a
                        href='https://form.asana.com/?k=UFeLGkl2Ortj6cEohzwSrA&d=1199244943732308'
                        target='_blank'
                        rel='noopener noreferrer'
                      >
                        <MessageSquareText aria-hidden />
                        {t('productFeedback')}
                      </a>
                    </Button>
                  </li>
                )}
                <li>
                  <div id='support-popup'>
                    <Button className='eye:w-full' variant='outline' asChild>
                      <RouterLink to='/help-center'>
                        <MessageCircleQuestion aria-hidden />
                        {t('helpCenter')}
                      </RouterLink>
                    </Button>
                  </div>
                </li>
              </ul>
            </SidebarFooter>
          )}
        </Sidebar>

        <div
          className={cn(
            'eye:min-w-0 eye:max-w-full',
            'eye:px-16 eye:pt-32 eye:sm:px-6 eye:sm:py-6 eye:lg:px-48 eye:lg:py-[40px]',
            'eye:flex eye:flex-col eye:flex-1 eye:flex-nowrap',
            'eye:overflow-x-clip eye:wrap-break-word',
            shouldShowDemoActions ? 'eye:pb-[100px]!' : 'eye:pb-32',
          )}
        >
          <SidebarInset className='eye:w-full eye:max-w-[1500px] eye:mx-auto'>
            <main
              id='main-content'
              tabIndex={-1}
              className='eye:relative eye:flex eye:w-full eye:flex-1 eye:flex-col eye:outline-none'
            >
              {renderMainContent()}
            </main>
            {/* Outside <main>, not inside it: a contentinfo landmark nested in
                main is not exposed as the page's footer. */}
            <Footer />
          </SidebarInset>
        </div>
      </div>

      {shouldShowDemoActions ? <FloatingDemoOfferActions /> : null}
    </div>
  );
}
